/* =============================================================================
 * M.M.LaFleur — collection card colour swatches.
 *
 * Puts the colour family back on the collection card and lets the shopper flip
 * between colourways in place, with the current colour always named beside the
 * dots.
 *
 * WHERE THE FAMILY COMES FROM
 * ---------------------------
 * From data-mm-family, server-rendered onto the card by mm_product_card.liquid
 * out of the product's own `exchange-*` collection.
 *
 * It used to be fetched from /products/<handle>?view=mm-swatches. That request
 * never returned JSON: the storefront canonicalises product URLs and strips
 * the query string, so it landed on the full product page as HTML, JSON.parse
 * threw, and the catch below returned an empty family. Every card in the grid
 * then fell through to withCurrent() and drew a single dot — the bug this file
 * now avoids rather than handles.
 *
 * The fetch is kept as a fallback for any caller that renders a card without
 * the attribute, but nothing on the collection grid depends on it any more.
 *
 * TWO IMAGES PER COLOURWAY
 * ------------------------
 * `image` is the hero, swapped into the card's main frame by select(). `swatch`
 * is the fabric close-up that fills the dot. They are separate fields because
 * they are separate pictures, and every attempt to serve both from one field
 * breaks an end: the dots previously drew `image` and rendered shrunken model
 * shots, and repointing that field at the swatch photo would have put a fabric
 * macro in the card's main frame on every colour change.
 *
 * SWATCH FILL AND ORDER
 * ---------------------
 * paint() takes the swatch photo first, then the hex the server resolved from
 * the mm_swatch metaobject, then the theme-level colour map, then a neutral
 * placeholder. The same ladder as the PDP row in mm_color_row.liquid, so a
 * colourway cannot look one way in the grid and another on its own page.
 *
 * It never falls back to `image`. A photograph shrunk to 10px is mostly
 * background and model, so a rail of them reads as a row of identical beige
 * smudges rather than as colours — and on a print or a tweed it is actively
 * misleading. Nothing resolved draws the placeholder instead: visibly unset
 * rather than confidently wrong.
 *
 * The map's line order is also the swatch order, matching the PDP row, so a
 * style shows its colours in the same sequence everywhere.
 * ========================================================================== */

(function () {
  "use strict";

  /* Platter re-injects the footer's scripts around a Barba container swap. */
  if (window.__mmCardSwatchesBound) return;
  window.__mmCardSwatchesBound = true;

  var MAX_DOTS = 8;
  var ROOT_MARGIN = "300px 0px";
  var UNLISTED = 10000;
  var PLACEHOLDER = "#d8d2c8";

  /* group key -> Promise of colour list. Shared across every card in the
     group and across the whole session. Only used by the fetch fallback;
     an inline family is already local to the card and costs nothing. */
  var GROUPS = {};

  var observer = null;
  var pending = null;
  var COLOR_MAP = null;
  var COLOR_ORDER = null;

  /* Published once per page by mm_footer.liquid from the theme setting, rather
     than repeated into all 24 cards. */
  function parseMap() {
    if (COLOR_MAP) return;
    COLOR_MAP = {};
    COLOR_ORDER = {};
    var seq = 0;
    var raw = window.__mmColorMap || "";
    raw.split("\n").forEach(function (line) {
      var bits = line.split("|");
      if (bits.length < 2) return;
      var key = bits[0].trim().toLowerCase();
      if (!key) return;
      COLOR_MAP[key] = bits[1].trim();
      if (!(key in COLOR_ORDER)) { COLOR_ORDER[key] = seq; seq += 1; }
    });
  }

  /* The card publishes only the 160px hero frame, to keep roughly 150
     characters per colourway out of every card in the grid. The grid-width
     frame the in-place swap needs is the same URL with a different width
     parameter. The swatch photo is not widened — it is only ever a dot. */
  function widen(url) {
    if (!url) return "";
    return /([?&])width=\d+/.test(url) ? url.replace(/([?&])width=\d+/, "$1width=800") : url;
  }

  function inlineFamily(row) {
    var raw = row.getAttribute("data-mm-family");
    if (!raw) return null;

    var parsed;
    try { parsed = JSON.parse(raw); } catch (e) { return null; }
    if (!parsed || !parsed.length) return null;

    return parsed.map(function (c) {
      return {
        name: c.name,
        url: c.url,
        image: c.image || "",
        image_lg: widen(c.image || ""),
        swatch: c.swatch || "",
        hex: c.hex || "",
        available: c.available !== false
      };
    });
  }

  function fetchFamily(handle) {
    var url = "/products/" + encodeURIComponent(handle) + "?view=mm-swatches";
    return fetch(url, { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (data) { return (data && data.colors) || []; })
      .catch(function () { return []; });
  }

  /* Server-rendered family first. Falls back to the endpoint, cached on the
     group key rather than the handle so several cards in one grid still share
     a single request. */
  function resolve(row) {
    var inline = inlineFamily(row);
    if (inline) return Promise.resolve(dedupe(inline));

    var handle = (row.getAttribute("data-handle") || "").trim();
    if (!handle) return Promise.resolve([]);

    var base = (row.getAttribute("data-group") || "").trim();
    var ygroup = (row.getAttribute("data-ygroup") || "").trim();
    var cacheKey = ygroup || base || handle;

    if (GROUPS[cacheKey]) return GROUPS[cacheKey];

    GROUPS[cacheKey] = fetchFamily(handle).then(dedupe);
    return GROUPS[cacheKey];
  }

  /* The colourway this card is actually showing, read from the card itself.

     A floor, not the main mechanism: the family should already contain it.
     It covers a style with no `exchange-*` collection at all, where the family
     comes back empty and the card would otherwise lose its own dot.

     `image` is taken from the card's own hero so the entry can still drive a
     swap; `swatch` is deliberately left empty. The hero is a model shot and
     has no business filling a dot — without a swatch the dot falls to the
     colour map, which is the right answer for this path.

     Deliberately NOT cached in GROUPS — that promise is shared by every card
     in the family, and seeding into it would leak one card's colour onto its
     siblings. The merge happens per card, after the shared lookup. */
  function withCurrent(row, colors) {
    var current = (row.getAttribute("data-current") || "").trim();
    if (!current) return colors;

    var key = current.toLowerCase();
    for (var i = 0; i < colors.length; i += 1) {
      if (colors[i] && colors[i].name && colors[i].name.toLowerCase() === key) {
        return colors;
      }
    }

    var handle = (row.getAttribute("data-handle") || "").trim();
    var card = row.closest ? row.closest(".mm-card") : null;
    var img = card ? card.querySelector(".mm-card-media img") : null;
    var src = img ? img.getAttribute("src") || "" : "";

    return colors.concat([{
      name: current,
      url: handle ? "/products/" + handle : "",
      image: src,
      image_lg: src,
      swatch: "",
      hex: "",
      available: true
    }]);
  }

  /* The catalogue carries separate extended-size records for some colours, so
     the same colourway can arrive twice. Merge on name. */
  function dedupe(colors) {
    var byName = {};
    var order = [];

    colors.forEach(function (c) {
      if (!c || !c.name) return;
      var key = c.name.toLowerCase();
      var seen = byName[key];
      if (!seen) {
        byName[key] = {
          name: c.name,
          url: c.url,
          image: c.image,
          image_lg: c.image_lg,
          swatch: c.swatch || "",
          hex: c.hex || "",
          available: c.available !== false
        };
        order.push(key);
        return;
      }
      if (c.url && c.url === seen.url) return;
      if (c.available !== false) {
        seen.available = true;
        if (!seen.url) seen.url = c.url;
      }
      if (!seen.image && c.image) seen.image = c.image;
      if (!seen.image_lg && c.image_lg) seen.image_lg = c.image_lg;
      /* A duplicate record may be the one carrying the swatch photo — the
         extended-size twin is often the fuller of the two. */
      if (!seen.swatch && c.swatch) seen.swatch = c.swatch;
      if (!seen.hex && c.hex) seen.hex = c.hex;
    });

    return order.map(function (k) { return byName[k]; });
  }

  /* Canonical order, shared with the PDP row: the colour map's line order,
     then anything not in the map alphabetically after it. */
  function sortByMap(list) {
    parseMap();
    return list.slice().sort(function (a, b) {
      var ai = COLOR_ORDER[a.name.toLowerCase()];
      var bi = COLOR_ORDER[b.name.toLowerCase()];
      if (ai === undefined) ai = UNLISTED;
      if (bi === undefined) bi = UNLISTED;
      if (ai !== bi) return ai - bi;
      return a.name.localeCompare(b.name);
    });
  }

  function setLabel(row, text) {
    var label = row.querySelector("[data-mm-card-color]");
    if (label) label.textContent = text || "";
  }

  /* Ladder, most specific first, matching mm_color_row.liquid. Each branch
     clears the property the other branch sets, because dots are reused across
     colours by select() and a stale backgroundImage would otherwise sit under
     a new backgroundColor.

     `colour.image` is never consulted. See the header. */
  function paint(dot, colour) {
    parseMap();

    if (colour.swatch) {
      dot.style.backgroundImage = 'url("' + colour.swatch + '")';
      dot.style.backgroundColor = "";
      dot.setAttribute("data-mm-swatch-src", "swatch-image");
      return;
    }

    if (colour.hex) {
      dot.style.backgroundColor = colour.hex;
      dot.style.backgroundImage = "";
      dot.setAttribute("data-mm-swatch-src", "metaobject-hex");
      return;
    }

    var mapped = COLOR_MAP[colour.name.toLowerCase()];
    if (mapped) {
      dot.style.backgroundColor = mapped;
      dot.style.backgroundImage = "";
      dot.setAttribute("data-mm-swatch-src", "legacy-map");
      return;
    }

    dot.style.backgroundColor = PLACEHOLDER;
    dot.style.backgroundImage = "";
    dot.setAttribute("data-mm-swatch-src", "none");
  }

  function select(row, colour, dots) {
    var card = row.closest(".mm-card");
    if (!card) return;

    /* Repoint every link on the card so the name, the image and Quick Add all
       lead to the colour now being shown. */
    Array.prototype.forEach.call(card.querySelectorAll("[data-mm-card-link]"), function (a) {
      if (colour.url) a.setAttribute("href", colour.url);
    });

    var quick = card.querySelector("[data-mm-quickadd]");
    if (quick && colour.url) {
      var handle = colour.url.split("/products/").pop().split("?")[0];
      if (handle) quick.setAttribute("data-mm-quickadd", handle);
    }

    /* The hero, never the swatch — swapping the main frame to a fabric macro
       would lose the garment. */
    paintMedia(card, colour);

    row.setAttribute("data-current", colour.name);
    setLabel(row, colour.name);

    Array.prototype.forEach.call(dots, function (d) {
      var on = d.getAttribute("data-colour") === colour.name;
      d.setAttribute("aria-pressed", String(on));
    });
  }

  /* --------------------------------------------------------------------- *
   * Carousel repaint on colour swap.
   *
   * The previous version kept the strip the shopper started on, pointed its
   * first frame at the new colour, then set display:none on every other slide
   * and on the arrows. Three things were wrong with that:
   *
   *   - .mm-card-slide crossfades on `opacity` (mm-shop.css). `display` is not
   *     part of that mechanism, so it was fighting it rather than driving it.
   *   - both were INLINE styles and nothing ever cleared them, so the carousel
   *     stayed dead for the life of the page — swatching back to the colour the
   *     card was rendered with did not bring it back.
   *   - stepCardMedia() bails when it sees fewer than two slides, so even the
   *     arrows that survived had nothing to step through.
   *
   * The fix is to give the new colour its own frames. The swatch endpoint only
   * carries featured_image, so the frames come from Shopify's native product
   * JSON instead — no theme file to keep in sync, and it is already public.
   * --------------------------------------------------------------------- */

  var MEDIA = {};

  /* Shopify returns protocol-relative CDN urls with no width. Reuse an
     existing width param when there is one rather than appending a second. */
  function sized(url, w) {
    if (!url) return "";
    if (url.indexOf("//") === 0) url = "https:" + url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, "$1width=" + w);
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + w;
  }

  /* Cached on the handle, so a grid where several cards swap to the same
     colourway costs one request rather than one per card. */
  function fetchMedia(url) {
    var handle = (url || "").split("/products/").pop().split("?")[0];
    if (!handle) return Promise.resolve([]);
    if (MEDIA[handle]) return MEDIA[handle];

    MEDIA[handle] = fetch("/products/" + handle + ".js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        return (p.images || []).map(function (u) { return sized(u, 800); });
      })
      .catch(function () { return []; });

    return MEDIA[handle];
  }

  /* Decode before showing. Assigning a new `src` to an <img> that is already on
     screen blanks it the instant the attribute changes and leaves it blank
     until the bytes arrive — which is the flicker. Decoding into a detached
     Image first means the swap happens on a frame where the picture is already
     ready to paint.

     decode() is not available everywhere and rejects on some cached-image
     paths in Safari, so onload/onerror is the floor and every outcome resolves.
     A colour swap must never be able to hang on a broken asset. */
  function preload(src) {
    return new Promise(function (resolve) {
      if (!src) { resolve(); return; }
      var img = new Image();
      var done = function () { resolve(); };
      img.onload = done;
      img.onerror = done;
      img.src = src;
      if (img.decode) img.decode().then(done).catch(done);
    });
  }

  function setFrame(slide, src, alt) {
    var img = slide.querySelector("img");
    if (!img) {
      /* The lead frame can be a video on some styles; a cloned video slide has
         no <img> to point at the new colour. */
      img = document.createElement("img");
      img.setAttribute("loading", "lazy");
      img.setAttribute("decoding", "async");
      slide.innerHTML = "";
      slide.appendChild(img);
    }
    /* srcset and sizes describe the OLD colour's asset and would win over the
       src being set here. */
    img.removeAttribute("srcset");
    img.removeAttribute("sizes");
    img.setAttribute("src", src);
    img.setAttribute("alt", alt);
  }

  function paintMedia(card, colour) {
    var media = card.querySelector(".mm-card-media");
    if (!media) return;

    var slides = [].slice.call(media.querySelectorAll("[data-mm-card-slide]"));
    if (!slides.length) return;

    var lead = colour.image_lg || colour.image;

    /* Clicking three swatches quickly starts three of these, and they finish in
       whatever order the network decides. Without a token the slowest one wins
       and the card ends up showing a colour nobody asked for last. */
    var token = (media.__mmPaintToken || 0) + 1;
    media.__mmPaintToken = token;

    /* PHASE 1 — the visible frame, once it can be painted without a gap.

       Nothing is touched before the decode resolves: the card goes on showing
       the previous colour rather than a white box. */
    preload(lead).then(function () {
      if (media.__mmPaintToken !== token) return;
      if (!lead) return;

      setFrame(slides[0], lead, colour.name);
      slides.forEach(function (s, i) {
        s.style.display = "";
        s.setAttribute("data-active", String(i === 0));
      });
    });

    /* PHASE 2 — the rest of the strip.

       Deliberately never re-touches slides[0]. Phase 1 has already put the
       right picture there, and rewriting its src a second time would reload
       and blank it — the flicker again, just later.

       The other slides sit at opacity 0 behind it (mm-shop.css), so they can be
       added, removed and repointed freely without anything being seen. */
    fetchMedia(colour.url).then(function (frames) {
      if (media.__mmPaintToken !== token) return;
      if (!frames || !frames.length) frames = lead ? [lead] : [];
      if (!frames.length) return;

      /* Extra nodes are REMOVED rather than hidden, because stepCardMedia()
         counts [data-mm-card-slide] to decide whether stepping is possible —
         hidden nodes would make it think there is more to show than there is. */
      while (slides.length < frames.length) {
        var clone = slides[0].cloneNode(true);
        clone.removeAttribute("data-mm-hero");
        clone.setAttribute("data-active", "false");
        media.appendChild(clone);
        slides.push(clone);
      }
      while (slides.length > frames.length) {
        var extra = slides.pop();
        if (extra.parentNode) extra.parentNode.removeChild(extra);
      }

      for (var i = 1; i < slides.length; i += 1) {
        slides[i].style.display = "";
        setFrame(slides[i], frames[i], colour.name);
      }

      var arrows = card.querySelector(".mm-card-arrows");
      if (arrows) arrows.style.display = frames.length > 1 ? "" : "none";
    });
  }

  function build(row, colors) {
    var wrap = row.querySelector("[data-mm-card-dots]");
    if (!wrap) return;

    var live = colors.filter(function (c) { return c.available !== false; });

    /* A single-colour style still gets its dot. It is not redundant with the
       name beside it — the pair is what tells the shopper this style comes in
       one colour, and it keeps the row visually aligned across the grid. */
    if (!live.length) {
      var current = row.getAttribute("data-current");
      if (!current) return;
      live = [{ name: current, url: "", image: "", swatch: "", hex: "", available: true }];
    }

    live = sortByMap(live);

    var currentKey = (row.getAttribute("data-current") || "").toLowerCase();
    wrap.innerHTML = "";

    var dots = [];

    live.slice(0, MAX_DOTS).forEach(function (c) {
      var dot = document.createElement("button");
      dot.type = "button";
      dot.className = "mm-dot";
      dot.setAttribute("data-colour", c.name);
      dot.setAttribute("title", c.name);
      dot.setAttribute("aria-label", c.name);
      dot.setAttribute("aria-pressed", String(c.name.toLowerCase() === currentKey));
      paint(dot, c);

      /* Hovering previews the name without committing to the colour, so a
         shopper can read the range without clicking through it. */
      dot.addEventListener("mouseenter", function () { setLabel(row, c.name); });
      dot.addEventListener("mouseleave", function () {
        setLabel(row, row.getAttribute("data-current"));
      });
      dot.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (!c.url) return;
        select(row, c, dots);
      });

      dots.push(dot);
      wrap.appendChild(dot);
    });

    if (live.length > MAX_DOTS) {
      var more = document.createElement("span");
      more.className = "mm-count mm-card-more";
      more.textContent = "+" + (live.length - MAX_DOTS);
      wrap.appendChild(more);
    }
  }

  function hydrate(row) {
    if (row.getAttribute("data-mm-done") === "1") return;
    row.setAttribute("data-mm-done", "1");

    resolve(row).then(function (colors) {
      build(row, withCurrent(row, colors || []));
    });
  }

  function init() {
    pending = null;

    var rows = document.querySelectorAll("[data-mm-card-swatches]:not([data-mm-done])");
    if (!rows.length) return;

    if (!("IntersectionObserver" in window)) {
      Array.prototype.forEach.call(rows, hydrate);
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          hydrate(entry.target);
        });
      }, { rootMargin: ROOT_MARGIN });
    }

    Array.prototype.forEach.call(rows, function (row) { observer.observe(row); });
  }

  function schedule() {
    if (pending) return;
    pending = window.requestAnimationFrame(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", schedule);
  window.addEventListener("popstate", schedule);

  /* Covers both Barba container swaps and cards appended by
     mm-infinite-scroll.js, neither of which fires an event this can listen
     for directly. */
  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }
}());
